import React, { useState, useEffect } from "react";
import { firebaseStorage, adminStorage } from "../utils/firebase";
import {
  Eye,
  Trash2,
  Check,
  X,
  Lock,
  BarChart3,
  Gift,
  Users,
  CreditCard,
  Calendar,
  AlertCircle,
  Phone,
  Music,
  Edit,
  Save,
} from "lucide-react";

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("isAdminAuthenticated") === "true";
  });

  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [adminPassword, setAdminPassword] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewGift, setPreviewGift] = useState(null);
  const [showMusicSettings, setShowMusicSettings] = useState(false);
  const [musicUrl, setMusicUrl] = useState("");
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [editingGift, setEditingGift] = useState(null);
  const [showDemoCreator, setShowDemoCreator] = useState(false);
  const [demoDay, setDemoDay] = useState(15);
  const [demoMessages, setDemoMessages] = useState([
    "أول يوم رمضان، وأول هدية بتفتح.. حبيت أقولك إن وجودك في حياتي هو أكبر فوز طلعت بيه. زي ما الشهر ده لسه بيبدأ، أنا عايز أبدأ معاكي عهد جديد إن كل يوم يكون أحلى من اللي قبله. ركزي في كل كلمة هتشوفيها الشهر ده، عشان كل حرف طالع من قلبي بجد. صوماً مقبولاً يا أجمل ما شافت عيني.",
    "تاني يوم رمضان.. والنهاردة حبيت أقولك إن أكتر حاجة بحبها في وجودك هي 'الأمان'. إنك الشخص اللي برتاح وأنا بحكي له كل حاجة. بدعي ربنا في تاني يوم إن قلبك يفضل دايماً مطمن وجمبي، وزي ما اليوم ده عدى بسلام، كل أيامنا الجاية تكون سوا وفي خير. بحبك جداً.",
    "تالت يوم رمضان.. والنهاردة وبدون أي مقدمات، حبيت أقولك إني كل يوم بشكر ربنا على 'صدفة' جمعتنا. وجودك في حياتي مش مجرد نصيب حلو، ده أعظم عوض. بدعي ربنا في تالت يوم إن علاقتنا تفضل دايماً في 'أولها' من ناحية الشغف، وفي أعماقها من ناحية الحب. بحبك يا ست البنات.",
    "رابع يوم رمضان.. والنهاردة حبيت أقولك إنك 'السكن' اللي بجد. اليوم اللي بيمر وإحنا مع بعض، حتى لو في عز التعب، بحس فيه بسكينة غريبة. بدعي ربنا في رابع يوم إنه يديمك في حياتي الروح اللي بتطمن قلبي، ويفضل وجودك هو الحاجة اللي بتهون عليا أي صعب. بحبك يا كل دنيتي.",
    "خامس يوم رمضان.. والنهاردة وأنا بفطر، سرحت في كل المرات اللي كنتي فيها جمبي وبتسنديني بكلمة بسيطة. حبيت أقولك إنك مش بس في بالي، إنتي بقيتي جزء من روتيني، من دعائي، ومن كل حاجة بعملها. بدعي ربنا في خامس يوم إنه يحفظك ليا من كل سوء، ويخليكي دايماً السند والضهر اللي مبيملش. بحبك جداً.",
  ]);
  
  const totalRevenue = gifts
    .filter((g) => !g.isDemo && g.paymentStatus === "approved")
    .reduce(
      (sum, g) => sum + Number(g.paymentProof?.amount || g.amount || 0),
      0,
    );

  useEffect(() => {
    loadAdminPassword();
    loadData();
    loadMusicSettings();
  }, []);

  const loadAdminPassword = async () => {
    const pwd = await adminStorage.getAdminPassword();
    setAdminPassword(pwd);
  };

  const loadData = async () => {
    setLoading(true);
    const allGifts = await firebaseStorage.getAllGifts();

    const sortedGifts = allGifts.sort((a, b) => {
      const dateA = a.createdAt?.toDate
        ? a.createdAt.toDate()
        : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate
        ? b.createdAt.toDate()
        : new Date(b.createdAt);
      return dateB - dateA;
    });

    const statsData = await firebaseStorage.getStats();
    setGifts(sortedGifts);
    setStats(statsData);
    setLoading(false);
  };

  const loadMusicSettings = async () => {
    const settings = await adminStorage.getSettings();
    setMusicUrl(settings.musicUrl || "");
    setMusicEnabled(settings.musicEnabled !== false);
  };

  const handleSaveMusicSettings = async () => {
    try {
      await adminStorage.updateSettings({ musicUrl, musicEnabled });
      setShowMusicSettings(false);
      alert("تم حفظ إعدادات الموسيقى! 🎵");
    } catch (error) {
      console.error("Error saving music settings:", error);
      alert("حدث خطأ أثناء حفظ الإعدادات");
    }
  };

  const handleEditGift = (gift) => {
    setEditingGift({
      ...gift,
      openingMessage: gift.openingMessage || "",
      lanterns: gift.lanterns || [],
    });
  };

  const handleSaveEditedGift = async () => {
    if (!editingGift) return;

    try {
      await firebaseStorage.updateGift(editingGift.id, {
        recipientName: editingGift.recipientName,
        senderName: editingGift.senderName,
        openingMessage: editingGift.openingMessage,
        lanterns: editingGift.lanterns,
      });

      setEditingGift(null);
      loadData();
      alert("تم تعديل الهدية بنجاح! ✅");
    } catch (error) {
      console.error("Error updating gift:", error);
      alert("حدث خطأ أثناء التعديل");
    }
  };

const handleCreateDemoGift = async () => {
  try {
    const allMessages = Array.from({ length: 30 }, (_, i) => {
      if (i < 5) {
        // أول 5 رسائل من المدخلات
        return {
          day: i + 1,
          message: demoMessages[i] || `رسالة اليوم ${i + 1}`,
          imageURL: i % 3 === 0 ? "https://dl.dropbox.com/scl/fi/w7sikdl7wvoctdqz0zfps/6ba83780179ae00b2d5a2adf332fc75f.jpg?rlkey=6d0vxzvmjd2nm24vvbpo2rao3&st=2a7bvpcz&dl=0" : "",
          duaa: i % 5 === 0 ? "اللهم بارك لنا في رمضان وبلغنا ليلة القدر 🌙" : "",
        };
      } else {
        // باقي الرسائل تلقائية
        return {
          day: i + 1,
          message: `رسالة اليوم ${i + 1} - هذه رسالة تجريبية لعرض شكل الهدايا اليومية ❤️`,
          imageURL: i % 3 === 0 ? "https://dl.dropbox.com/scl/fi/h76m4sjidt1xm3uklgf7a/43f8c69ea6e52909841335b414ae8fe2.jpg?rlkey=ajw38c8zcr39iw2l61psl18np&st=d9azxoqi&dl=0" : "",
          duaa: i % 5 === 0 ? "اللهم بارك لنا في رمضان وبلغنا ليلة القدر 🌙" : "",
        };
      }
    });

    const demoGift = {
      recipientName: "Maha",
      senderName: "Ahmed",
      relationship: "custom",
      openingMessage: "رمضان السنة دي مش مجرد شهر، ده أول رمضان وقلبي مليان بيكي. وعشان إنتي مش أي حد، هديتك كمان مش أي هدية.. الهدية دي هتعيش معاكي كل يوم، كل هدية هنا مخبية ليكى حتة من قلبي، كلمة مقلتهاش، أو دعوة ليكي وقت الفطار. افتحي فانوس كل يوم، وخلينا نعيد ذكرياتنا سوا. رمضانك جنة يا حبيبتي.",
      lanterns: allMessages,
      planType: "premium",
      paid: true,
      paymentStatus: "approved",
      isDemo: true,
      demoDay: demoDay,
      createdAt: Date.now(),
    };

    const result = await firebaseStorage.saveGift(demoGift);
    const demoUrl = `${window.location.origin}?gift=${result.id}`;
    
    navigator.clipboard.writeText(demoUrl);
    
    alert(`تم إنشاء الهدية التجريبية! ✅\n\nالرابط تم نسخه للحافظة:\n${demoUrl}\n\nاليوم الحالي: ${demoDay}\nجميع الأيام حتى ${demoDay} مفتوحة!`);
    
    setShowDemoCreator(false);
    loadData();
  } catch (error) {
    console.error("Error creating demo gift:", error);
    alert("حدث خطأ أثناء إنشاء الهدية التجريبية");
  }
};  
  const handleLogin = () => {
    if (password === adminPassword) {
      setIsAuthenticated(true);
      localStorage.setItem("isAdminAuthenticated", "true");
    } else {
      alert("كلمة المرور غلط!");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("isAdminAuthenticated");
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      alert("كلمة المرور لازم تكون 6 أحرف على الأقل");
      return;
    }
    try {
      await adminStorage.updateAdminPassword(newPassword);
      setAdminPassword(newPassword);
      setNewPassword("");
      setShowChangePassword(false);
      alert("تم تغيير كلمة المرور بنجاح! 🔐");
    } catch (error) {
      console.error(error);
      alert("حصلت مشكلة وأنا بغير الباسورد");
    }
  };

  const handleDeleteGift = async (id) => {
    if (window.confirm("متأكد إنك عايز تحذف الهدية دي؟")) {
      await firebaseStorage.deleteGift(id);
      loadData();
    }
  };

  const handlePaymentAction = async (id, action) => {
    if (action === "approve") {
      await firebaseStorage.updateGift(id, {
        paymentStatus: "approved",
        rejectionReason: null,
      });
    } else {
      const reason = window.prompt("برجاء إدخال سبب الرفض للعميل:");
      if (reason === null) return;
      await firebaseStorage.updateGift(id, {
        paymentStatus: "rejected",
        rejectionReason: reason || "لم يتم تحديد سبب",
      });
    }
    loadData();
  };

  const viewGift = (id) => {
    window.open(`?gift=${id}`, "_blank");
  };

  const sendWhatsAppSuccess = (gift) => {
    const phone = gift.paymentProof?.senderNumber;
    if (!phone) {
      alert("رقم العميل غير متوفر في بيانات الدفع!");
      return;
    }
    const cleanPhone = phone.replace(/\D/g, "");
    const siteURL = window.location.origin;
    const message = `أهلاً يا ${gift.senderName || "فنان"}، تم تفعيل هديتك لـ *${gift.recipientName}* بنجاح! ❤️ تقدر تفتحها من اللينك ده: ${siteURL}/?gift=${gift.id}
    ومتنساش تعملنا فولو على تيك توك لمتابعة كل جديد https://www.tiktok.com/@two_2_souls`;
    const finalPhone = cleanPhone.startsWith("2")
      ? cleanPhone
      : `2${cleanPhone}`;
    window.open(
      `https://wa.me/${finalPhone}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const filteredGifts = gifts.filter((gift) => {
    if (gift.paymentStatus === "rejected") return false;
    if (gift.paymentStatus === "pending") return false;
    const matchesSearch =
      gift.recipientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gift.paymentProof?.senderNumber?.includes(searchTerm);
    if (filter === "all") return matchesSearch;
    return gift.planType === filter && matchesSearch;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "غير متوفر";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white flex items-center justify-center px-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full border border-white/10">
          <div className="text-center mb-6">
            <Lock className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h2 className="text-2xl font-bold">دخول الإدارة</h2>
          </div>
          <input
            name="password"
            type="password"
            placeholder="كلمة المرور"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 mb-4 text-center"
          />
          <button
            onClick={handleLogin}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg transition-all"
          >
            دخول
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-yellow-300 animate-pulse" />
          <p className="text-xl">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const pendingPayments = gifts.filter((g) => !g.isDemo && g.paymentStatus === "pending");
  const rejectedPayments = gifts.filter((g) => !g.isDemo && g.paymentStatus === "rejected");

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white py-8 px-4 text-right"
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto">
        <div className="relative z-10 flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">لوحة الإدارة</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDemoCreator(true)}
              className="relative z-50 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-all border border-green-500/30 shadow-lg"
            >
              <Gift className="w-4 h-4" />
              <span className="hidden md:inline text-sm font-bold">
                هدية تجريبية
              </span>
            </button>

            <button
              onClick={() => setShowMusicSettings(true)}
              className="relative z-50 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-all border border-purple-500/30"
            >
              <Music className="w-4 h-4" />
              <span className="hidden md:inline text-sm font-bold">
                الموسيقى
              </span>
            </button>
            <button
              onClick={() => setShowChangePassword(true)}
              className="relative z-50 bg-white/10 hover:bg-white/20 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-all border border-white/10"
            >
              <Lock className="w-4 h-4" />
              <span className="hidden md:inline text-sm font-bold">
                تغيير الباسوورد
              </span>
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-bold py-2 px-4 rounded-lg"
            >
              خروج
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center border border-white/5">
            <Users className="w-8 h-8 mx-auto mb-2 text-blue-400" />
            <p className="text-2xl font-bold">{stats.freeUsers || 0}</p>
            <p className="text-purple-200 text-sm">هدايا مجاني</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center border border-white/5">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p className="text-2xl font-bold">{stats.basicPlans || 0}</p>
            <p className="text-purple-200 text-sm">هدايا بيزك</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center border border-white/5">
            <Gift className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
            <p className="text-2xl font-bold">{stats.premiumPlans || 0}</p>
            <p className="text-purple-200 text-sm">هدايا بلس</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center border border-white/5">
            <X className="w-8 h-8 mx-auto mb-2 text-red-400" />
            <p className="text-2xl font-bold">
              {gifts.filter((g) => g.paymentStatus === "rejected").length || 0}
            </p>
            <p className="text-purple-200 text-sm">هدايا مرفوضة</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center border border-white/5">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 text-red-400" />
            <p className="text-2xl font-bold">{stats.pendingPayments || 0}</p>
            <p className="text-purple-200 text-sm">دفع معلق</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-lg rounded-lg p-6 text-center border border-green-500/30">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p className="text-2xl font-bold text-green-400">
              {totalRevenue} ج.م
            </p>
            <p className="text-white/60 text-sm">إجمالي الأرباح</p>
          </div>
        </div>

        {/* Pending Payments Section */}
        {pendingPayments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              طلبات الدفع المعلقة{" "}
              <span className="bg-yellow-500 text-indigo-950 text-xs px-2 py-1 rounded-full">
                {pendingPayments.length}
              </span>
            </h2>
            <div className="space-y-4">
              {pendingPayments.map((gift) => (
                <div
                  key={gift.id}
                  className="bg-white/10 backdrop-blur-lg rounded-xl p-4 md:p-6 flex justify-between items-center border-r-4 border-yellow-500 gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-bold text-lg truncate">
                        {gift.recipientName}
                      </p>
                      <span className="text-[10px] bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded font-mono">
                        {gift.trackingCode || "N/A"}
                      </span>
                    </div>

                    <div className="space-y-0.5">
                      <p className="text-purple-200 text-sm">
                        {gift.planType === "premium"
                          ? "بلس"
                          : gift.planType === "basic"
                            ? "بيزك"
                            : "مجاني"}{" "}
                        - {gift.paymentProof?.amount || gift.amount || 0} جنيه
                      </p>
                      <p className="text-xs text-yellow-300 font-mono truncate">
                        العملية:{" "}
                        {gift.paymentProof?.senderNumber ||
                          gift.transactionId ||
                          "غير معروف"}
                      </p>
                      <p className="text-[10px] text-white/40 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />{" "}
                        {formatDate(gift.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 md:flex md:w-auto md:mt-0">
                    <button
                      onClick={() => setPreviewGift(gift)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white p-3 md:px-4 md:py-2 rounded-lg flex items-center justify-center gap-1 transition-all"
                    >
                      <Eye className="w-5 h-5" />
                      <span className="hidden md:inline font-bold">معاينه</span>
                    </button>
                    <button
                      onClick={() => viewGift(gift.id)}
                      className="bg-blue-500 hover:bg-blue-600 text-white p-3 md:px-4 md:py-2 rounded-lg flex items-center justify-center gap-1 transition-all"
                    >
                      <Eye className="w-5 h-5" />
                      <span className="hidden md:inline font-bold">فتح</span>
                    </button>
                    <button
                      onClick={() => handlePaymentAction(gift.id, "approve")}
                      className="bg-green-500 hover:bg-green-600 text-white p-3 md:px-4 md:py-2 rounded-lg flex items-center justify-center gap-1 transition-all"
                    >
                      <Check className="w-5 h-5 md:w-4 md:h-4" />
                      <span className="hidden md:inline font-bold">قبول</span>
                    </button>
                    <button
                      onClick={() => handlePaymentAction(gift.id, "reject")}
                      className="bg-red-500 hover:bg-red-600 text-white p-3 md:px-4 md:py-2 rounded-lg flex items-center justify-center gap-1 transition-all"
                    >
                      <X className="w-5 h-5 md:w-4 md:h-4" />
                      <span className="hidden md:inline font-bold">رفض</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rejected Payments Section */}
        {rejectedPayments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-red-400">
              الطلبات المرفوضة ({rejectedPayments.length})
            </h2>
            <div className="space-y-4">
              {rejectedPayments.map((gift) => (
                <div
                  key={gift.id}
                  className="bg-red-500/5 backdrop-blur-lg rounded-xl p-4 border-r-4 border-red-500 flex justify-between items-center opacity-90 transition-all hover:opacity-100"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-bold text-lg">{gift.recipientName}</p>
                      <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded font-mono">
                        {gift.trackingCode}
                      </span>
                      <span className="text-sm font-bold text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                        {gift.paymentProof?.amount || gift.amount || 0} جنيه
                      </span>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-red-300 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        السبب: {gift.rejectionReason || "غير محدد"}
                      </p>
                      <p className="text-[10px] text-white/30 font-mono">
                        العملية:{" "}
                        {gift.paymentProof?.senderNumber || "غير معروف"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 mr-4">
                    <button
                      onClick={() => handlePaymentAction(gift.id, "approve")}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                      title="إعادة قبول الطلب"
                    >
                      <Check className="w-3 h-3" />
                      <span className="hidden md:inline text-sm font-bold">
                        إعادة قبول
                      </span>
                    </button>
                    <button
                      onClick={() => handleDeleteGift(gift.id)}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all"
                      title="حذف نهائي"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Gifts Section */}
        <div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 className="text-2xl font-bold">
              كل الهدايا ({filteredGifts.length})
            </h2>
            <input
              name="search"
              type="text"
              placeholder="ابحث بالاسم أو الرقم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 px-4 py-2 rounded-lg bg-white/10 border border-white/20 mb-4 md:mb-0"
            />
            <div className="flex bg-white/10 p-1 rounded-lg self-end md:self-auto overflow-x-auto">
              {["all", "free", "basic", "premium"].map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-4 py-1.5 rounded-md text-sm whitespace-nowrap transition-all ${filter === t ? "bg-yellow-500 text-indigo-950 font-bold" : "hover:bg-white/10"}`}
                >
                  {t === "all"
                    ? "الكل"
                    : t === "free"
                      ? "مجاني"
                      : t === "basic"
                        ? "بيزك"
                        : "بلس"}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {filteredGifts.map((gift) => (
              <div
                key={gift.id}
                className="bg-white/10 backdrop-blur-lg rounded-lg p-6 flex flex-col md:flex-row justify-between items-start md:items-center hover:bg-white/15 transition-all border border-white/5 gap-4"
              >
                <div className="flex-1 w-full text-right">
                  <p className="font-bold text-xl text-yellow-300">
                    إلى: {gift.recipientName}
                    {gift.isDemo && (
                      <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1 rounded-full font-bold flex items-center gap-1 animate-pulse">
                        <Gift className="w-3 h-3" />
                        DEMO - يوم {gift.demoDay}
                      </span>
                    )}
                  </p>
                    
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 mt-3 bg-black/30 p-4 rounded-xl border border-white/10">
                    <p className="text-sm">
                      <span className="text-purple-300 font-semibold">من:</span>{" "}
                      {gift.senderName || gift.sender || "غير متوفر"}
                    </p>
                    <p className="text-sm">
                      <span className="text-purple-300 font-semibold">
                        التليفون:
                      </span>{" "}
                      {gift.paymentProof?.senderNumber ||
                        gift.phone ||
                        gift.phoneNumber ||
                        "مجاني"}
                    </p>
                    <p className="text-sm">
                      <span className="text-purple-300 font-semibold">
                        المبلغ:
                      </span>{" "}
                      <span className="text-green-400 font-bold">
                        {gift.paymentProof?.amount || gift.amount || 0} جنيه
                      </span>
                    </p>
                    <p className="text-sm flex items-center gap-1">
                      <span className="text-purple-300 font-semibold">
                        التاريخ:
                      </span>{" "}
                      {formatDate(gift.createdAt)}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-3 items-center">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${gift.planType === "premium" ? "bg-yellow-500/20 text-yellow-300" : gift.planType === "basic" ? "bg-blue-500/20 text-blue-300" : "bg-gray-500/20 text-gray-300"}`}
                    >
                      {gift.planType === "premium"
                        ? "بلس"
                        : gift.planType === "basic"
                          ? "بيزك"
                          : "مجاني"}
                    </span>
                    <span className="text-purple-200 text-sm">
                      {gift.lanterns?.length || 0} فانوس
                    </span>
                    <span
                      className={`text-xs ${gift.paymentStatus === "approved" ? "text-green-400" : gift.paymentStatus === "rejected" ? "text-red-400" : "text-yellow-400"}`}
                    >
                      •{" "}
                      {gift.paymentStatus === "approved"
                        ? "تم الدفع"
                        : gift.paymentStatus === "rejected"
                          ? "مرفوض"
                          : "قيد الانتظار"}
                      {(gift.paymentStatus === "rejected" ||
                        gift.paymentStatus === "pending") && (
                        <span className="mr-2 bg-white/10 px-1.5 py-0.5 rounded font-mono text-[10px]">
                          {gift.trackingCode}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex flex-row gap-2 w-full mt-4 md:mt-0 md:w-auto justify-center md:justify-end">
                  <button
                    onClick={() => handleEditGift(gift)}
                    className="flex-1 md:flex-none bg-yellow-600 hover:bg-yellow-700 text-white p-3 md:py-2 md:px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
                  >
                    <Edit className="w-5 h-5 md:w-4 md:h-4" />
                    <span className="hidden md:inline text-sm font-bold">
                      تعديل
                    </span>
                  </button>

                  <button
                    onClick={() => sendWhatsAppSuccess(gift)}
                    className="flex-1 md:flex-none bg-green-600 hover:bg-green-700 text-white p-3 md:py-2 md:px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
                  >
                    <Phone className="w-5 h-5 md:w-4 md:h-4" />
                    <span className="hidden md:inline text-sm font-bold">
                      واتساب
                    </span>
                  </button>

                  <button
                    onClick={() => viewGift(gift.id)}
                    className="flex-1 md:flex-none bg-blue-500 hover:bg-blue-600 text-white p-3 md:py-2 md:px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
                  >
                    <Eye className="w-5 h-5 md:w-4 md:h-4" />
                    <span className="hidden md:inline text-sm font-bold">
                      فتح
                    </span>
                  </button>
                  <button
                    onClick={() => handleDeleteGift(gift.id)}
                    className="flex-1 md:flex-none bg-red-500 hover:bg-red-600 text-white p-3 md:py-2 md:px-4 rounded-lg flex items-center justify-center gap-2 border border-red-500/30 transition-all"
                  >
                    <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                    <span className="hidden md:inline text-sm font-bold">
                      حذف
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Developer Credit */}
        <div className="mt-8 group">
          <a
            href="https://www.tiktok.com/@two_2_souls"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 opacity-30 hover:opacity-100 transition-all"
          >
            <span className="text-[10px] tracking-[0.4em] text-gray-400 uppercase font-light">
              Developed by
            </span>
            <span className="text-white text-xs tracking-[0.3em] font-light group-hover:text-[#d4af37]">
              TWO <span className="font-bold">SOULS</span>
            </span>
          </a>
        </div>
      </div>

      {/* Preview Gift Modal */}
      {previewGift && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[10000] p-4 md:p-10 overflow-y-auto text-right"
          dir="rtl"
        >
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
              <div>
                <h2 className="text-3xl font-bold text-yellow-400">
                  معاينة: {previewGift.recipientName}
                </h2>
                <p className="text-purple-200">
                  الرسالة الافتتاحية: {previewGift.openingMessage}
                </p>
              </div>
              <button
                onClick={() => setPreviewGift(null)}
                className="p-2 bg-white/10 rounded-full hover:bg-red-500 transition-all"
              >
                <X size={32} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {previewGift.lanterns.map((l, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-xl p-3 border border-white/10"
                >
                  <p className="text-xs font-bold text-yellow-300 mb-2 text-center">
                    يوم {l.day}
                  </p>
                  {l.imageURL ? (
                    <img
                      src={l.imageURL}
                      className="w-full h-32 object-cover rounded-lg mb-2 shadow-lg"
                      alt="preview"
                    />
                  ) : (
                    <div className="w-full h-32 bg-white/5 rounded-lg mb-2 flex items-center justify-center text-[10px] text-white/20 italic">
                      بدون صورة
                    </div>
                  )}
                  <p className="text-[10px] line-clamp-3 leading-relaxed opacity-70">
                    {l.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Gift Modal */}
      {editingGift && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[10000] p-4 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div
              className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-8 text-right"
              dir="rtl"
            >
              <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
                <h2 className="text-3xl font-bold text-yellow-400">
                  تعديل الهدية
                </h2>
                <button
                  onClick={() => setEditingGift(null)}
                  className="p-2 bg-white/10 rounded-full hover:bg-red-500 transition-all"
                >
                  <X size={32} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2 text-yellow-300">
                      اسم المرسل
                    </label>
                    <input
                      type="text"
                      value={editingGift.senderName}
                      onChange={(e) =>
                        setEditingGift({
                          ...editingGift,
                          senderName: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 text-yellow-300">
                      اسم المستلم
                    </label>
                    <input
                      type="text"
                      value={editingGift.recipientName}
                      onChange={(e) =>
                        setEditingGift({
                          ...editingGift,
                          recipientName: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                </div>

                {/* Opening Message */}
                <div>
                  <label className="block text-sm mb-2 text-yellow-300">
                    رسالة الافتتاح
                  </label>
                  <textarea
                    value={editingGift.openingMessage}
                    onChange={(e) =>
                      setEditingGift({
                        ...editingGift,
                        openingMessage: e.target.value,
                      })
                    }
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Lanterns */}
                <div>
                  <h3 className="text-xl font-bold mb-4 text-yellow-300">
                    الهدايا اليومية
                  </h3>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {editingGift.lanterns.map((lantern, idx) => (
                      <div
                        key={idx}
                        className="bg-white/5 rounded-lg p-4 border border-white/10"
                      >
                        <p className="text-sm font-bold text-yellow-200 mb-2">
                          يوم {lantern.day}
                        </p>
                        <textarea
                          value={lantern.message}
                          onChange={(e) => {
                            const newLanterns = [...editingGift.lanterns];
                            newLanterns[idx] = {
                              ...newLanterns[idx],
                              message: e.target.value,
                            };
                            setEditingGift({
                              ...editingGift,
                              lanterns: newLanterns,
                            });
                          }}
                          rows={3}
                          className="w-full px-3 py-2 rounded-lg bg-white/20 border border-white/20 text-white text-sm focus:outline-none focus:border-yellow-400"
                          placeholder="رسالة اليوم..."
                        />
                        {lantern.imageURL && (
                          <div className="mt-2">
                            <input
                              type="text"
                              value={lantern.imageURL}
                              onChange={(e) => {
                                const newLanterns = [...editingGift.lanterns];
                                newLanterns[idx] = {
                                  ...newLanterns[idx],
                                  imageURL: e.target.value,
                                };
                                setEditingGift({
                                  ...editingGift,
                                  lanterns: newLanterns,
                                });
                              }}
                              className="w-full px-3 py-2 rounded-lg bg-white/20 border border-white/20 text-white text-sm focus:outline-none focus:border-yellow-400"
                              placeholder="رابط الصورة..."
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setEditingGift(null)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSaveEditedGift}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    حفظ التعديلات
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Music Settings Modal */}
      {showMusicSettings && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[9999]">
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl p-8 max-w-md w-full border border-white/20 shadow-2xl">
            <h3 className="text-2xl font-bold mb-6 text-center text-white flex items-center justify-center gap-2">
              <Music className="w-6 h-6" />
              إعدادات الموسيقى
            </h3>

            <div className="space-y-6">
              {/* Music URL */}
              <div>
                <label className="block text-sm mb-2 text-purple-200">
                  رابط الموسيقى (MP3)
                </label>
                <input
                  type="url"
                  value={musicUrl}
                  onChange={(e) => setMusicUrl(e.target.value)}
                  placeholder="https://example.com/music.mp3"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400"
                />
                <p className="text-xs text-purple-300 mt-2">
                  💡 ضع رابط مباشر لملف MP3
                </p>
              </div>

              {/* Enable/Disable Toggle */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <label className="text-white font-bold">تفعيل الموسيقى</label>
                  <button
                    onClick={() => setMusicEnabled(!musicEnabled)}
                    className={`relative w-14 h-7 rounded-full transition-all ${
                      musicEnabled ? "bg-green-500" : "bg-gray-600"
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        musicEnabled ? "transform translate-x-7" : ""
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-purple-200 mt-2">
                  {musicEnabled ? "🎵 الموسيقى مفعلة" : "🔇 الموسيقى متوقفة"}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowMusicSettings(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg transition-all"
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveMusicSettings}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg transition-all"
              >
                حفظ الإعدادات
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[9999]">
          <div className="bg-indigo-900 rounded-xl p-8 max-w-md w-full border border-white/20 shadow-2xl">
            <h3 className="text-xl font-bold mb-4 text-center text-white">
              تغيير كلمة المرور
            </h3>
            <input
              name="newPassword"
              type="password"
              placeholder="كلمة المرور الجديدة"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white/10 mb-4 text-center border border-white/20 text-white focus:border-yellow-400 outline-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleChangePassword}
                className="flex-1 bg-yellow-400 text-indigo-900 font-bold py-3 rounded-lg hover:bg-yellow-500 transition-all"
              >
                حفظ التغيير
              </button>
              <button
                onClick={() => setShowChangePassword(false)}
                className="flex-1 bg-white/10 text-white font-bold py-3 rounded-lg hover:bg-white/20 transition-all"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
      {showDemoCreator && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[9999] overflow-y-auto">
          <div className="bg-gradient-to-br from-green-900 to-emerald-900 rounded-xl p-8 max-w-2xl w-full border border-green-500/30 shadow-2xl my-8">
            <h3 className="text-2xl font-bold mb-6 text-center text-white flex items-center justify-center gap-2">
              <Gift className="w-6 h-6" />
              إنشاء هدية تجريبية
            </h3>
      
            <div className="space-y-6">
              <div className="bg-white/10 rounded-lg p-4 border border-white/10">
                <p className="text-sm text-green-200 mb-3">
                  💡 هذه الهدية ستكون مفتوحة لكل الأيام حتى اليوم المحدد
                </p>
                <label className="block text-sm mb-2 text-green-100 font-bold">
                  اليوم الحالي في الهدية التجريبية
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={demoDay}
                  onChange={(e) => setDemoDay(parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white text-center text-2xl font-bold focus:outline-none focus:border-green-400"
                />
                <p className="text-xs text-green-300 mt-2">
                  🌙 جميع الأيام من 1 إلى {demoDay} ستكون مفتوحة
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-4 max-h-80 overflow-y-auto">
                <p className="text-sm text-blue-200 mb-3 font-bold flex items-center gap-2">
                  ✍️ الرسائل الخمسة الأولى (اكتبها بنفسك)
                </p>
                {[0, 1, 2, 3, 4].map((index) => (
                  <div key={index} className="mb-3">
                    <label className="block text-xs text-blue-100 mb-1">
                      📅 اليوم {index + 1}
                    </label>
                    <textarea
                      value={demoMessages[index]}
                      onChange={(e) => {
                        const newMessages = [...demoMessages];
                        newMessages[index] = e.target.value;
                        setDemoMessages(newMessages);
                      }}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-blue-400/30 text-white text-sm focus:outline-none focus:border-blue-400 resize-none"
                      placeholder={`رسالة اليوم ${index + 1}...`}
                    />
                  </div>
                ))}
                <p className="text-xs text-blue-300 mt-2 italic">
                  💡 باقي الأيام (6-30) سيتم ملؤها تلقائياً
                </p>
              </div>
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
                <p className="text-sm text-yellow-200">
                  ⚠️ <strong>ملاحظة:</strong> الهدية التجريبية ستحتوي على:
                </p>
                <ul className="text-xs text-yellow-100 mt-2 space-y-1 list-disc list-inside">
                  <li>30 يوم كاملين بالرسائل</li>
                  <li>صور تجريبية كل 3 أيام</li>
                  <li>أدعية كل 5 أيام</li>
                  <li>خطة Premium</li>
                </ul>
              </div>
            </div>
      
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowDemoCreator(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg transition-all"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateDemoGift}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                <Gift className="w-5 h-5" />
                إنشاء الهدية
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
